/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import ButtonComponent from '../Button/button';
import CalendarIcon from '../../Assets/images/svg/dar_calendar_blank_gray.svg';
import './dateRangeInput.scss';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const DateRangeInput = ({
  label = 'Date',
  placeholder = 'Select Date Range',
  fromLabel = 'Choose Date - From',
  toLabel = 'Choose Date - To',
  isRequired = false,
  showFromTo = true,
  fromValue = null,
  toValue = null,
  onChange,
  onApply,
  onReset,
  minYear,
  maxYear,
  minMonth,
  maxMonth,
  minDay,
  maxDay,
  dateFormat = 'DD-MMM-YYYY',
  saveOnOutsideClick = true,
}) => {
  const today = dayjs();
  const wrapperRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(today.month());
  const [viewYear, setViewYear] = useState(today.year());
  const [activeField, setActiveField] = useState('from');
  const [internalFrom, setInternalFrom] = useState(fromValue);
  const [internalTo, setInternalTo] = useState(toValue);

  // Sync internal states and calendar panel view when prop values change or reset
  useEffect(() => {
    setInternalFrom(fromValue);
    setInternalTo(toValue);

    if (fromValue && dayjs(fromValue).isValid()) {
      setViewMonth(dayjs(fromValue).month());
      setViewYear(dayjs(fromValue).year());
    } else {
      setViewMonth(today.month());
      setViewYear(today.year());
    }
  }, [fromValue, toValue]);

  // Create a mutable state reference to avoid stale closures in the event listener
  const stateRef = useRef({ internalFrom, internalTo, onApply, fromValue, toValue, saveOnOutsideClick });

  useEffect(() => {
    stateRef.current = { internalFrom, internalTo, onApply, fromValue, toValue, saveOnOutsideClick };
  }, [internalFrom, internalTo, onApply, fromValue, toValue, saveOnOutsideClick]);

  // Handle outside click event listener logic
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isMuiMenu
    = e.target.closest('.MuiPopover-root')
    || e.target.closest('.MuiMenu-paper')
    || e.target.closest('.MuiMenuItem-root');

      if (isMuiMenu) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        const current = stateRef.current;

        if (current.saveOnOutsideClick) {
          // Check if the user actually clicked/changed anything on the calendar UI
          const didSelectionChange
            = (!current.fromValue && current.internalFrom)
            || (current.fromValue && !current.fromValue.isSame(current.internalFrom, 'day'))
            || (!current.toValue && current.internalTo)
            || (current.toValue && !current.toValue.isSame(current.internalTo, 'day'));

          if (didSelectionChange && current.onApply) {
            current.onApply(current.internalFrom, current.internalTo);
          } else if (!didSelectionChange) {
            setInternalFrom(current.fromValue);
            setInternalTo(current.toValue);
          }
        } else {
          // If saveOnOutsideClick is false, discard changes and revert state
          setInternalFrom(current.fromValue);
          setInternalTo(current.toValue);
        }

        setActiveField('from');
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const yearOptions = useMemo(() => {
    const start = minYear ?? today.year() - 10;
    const end = maxYear ?? today.year() + 2;
    const years = [];
    for (let y = start; y <= end; y++) years.push(y);
    return years;
  }, [minYear, maxYear, today]);

  const monthOptions = useMemo(() => {
    const startIdx = minMonth !== undefined ? minMonth - 1 : 0;
    const endIdx = maxMonth !== undefined ? maxMonth - 1 : 11;
    return MONTHS.slice(startIdx, endIdx + 1).map((name, i) => ({
      name,
      index: startIdx + i,
    }));
  }, [minMonth, maxMonth]);

  const calendarDays = useMemo(() => {
    const firstDayOfWeek = dayjs(new Date(viewYear, viewMonth, 1)).day();
    const daysInMonth = dayjs(new Date(viewYear, viewMonth, 1)).daysInMonth();
    const days = [];
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [viewMonth, viewYear]);

  const normalizeBoundaryDate = (value) => {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.startOf('day') : null;
  };

  const normalizedMinDay = useMemo(() => normalizeBoundaryDate(minDay), [minDay]);
  const normalizedMaxDay = useMemo(() => normalizeBoundaryDate(maxDay), [maxDay]);

  const getDateForDay = (day) => dayjs(new Date(viewYear, viewMonth, day));

  const isDaySelected = (day) => {
    if (!day) return false;
    const date = getDateForDay(day);
    return (
      (internalFrom && date.isSame(internalFrom, 'day'))
      || (internalTo && date.isSame(internalTo, 'day'))
    );
  };

  const isDayInRange = (day) => {
    if (!day || !internalFrom || !internalTo) return false;
    const date = getDateForDay(day);
    return date.isAfter(internalFrom, 'day') && date.isBefore(internalTo, 'day');
  };

  const isRangeStart = (day) => {
    if (!day || !internalFrom || !internalTo) return false;
    return getDateForDay(day).isSame(internalFrom, 'day');
  };

  const isRangeEnd = (day) => {
    if (!day || !internalFrom || !internalTo) return false;
    return getDateForDay(day).isSame(internalTo, 'day');
  };

  const isDayDisabled = (day) => {
    if (!day) return true;
    const date = getDateForDay(day).startOf('day');

    if (normalizedMinDay && date.isBefore(normalizedMinDay, 'day')) {
      return true;
    }

    if (normalizedMaxDay && date.isAfter(normalizedMaxDay, 'day')) {
      return true;
    }

    return false;
  };

  const handleDayClick = (day) => {
    if (!day || isDayDisabled(day)) return;
    const clicked = getDateForDay(day);

    if (!showFromTo) {
      setInternalFrom(clicked);
      setInternalTo(null);
      setActiveField('from');
      if (onChange) onChange(clicked, null);
      return;
    }

    if (activeField === 'from') {
      const newTo = internalTo && clicked.isAfter(internalTo) ? null : internalTo;
      setInternalFrom(clicked);
      setInternalTo(newTo);
      setActiveField('to');
      if (onChange) onChange(clicked, newTo);
    } else {
      if (internalFrom && clicked.isBefore(internalFrom, 'day')) {
        setInternalFrom(clicked);
        setInternalTo(null);
        setActiveField('to');
        if (onChange) onChange(clicked, null);
      } else {
        setInternalTo(clicked);
        setActiveField('from');
        if (onChange) onChange(internalFrom, clicked);
      }
    }
  };

  const handleReset = () => {
    setInternalFrom(null);
    setInternalTo(null);
    setActiveField('from');
    if (onChange) onChange(null, null);
    if (onReset) onReset();
    setIsOpen(false);
  };

  const handleApply = () => {
    if (onApply) onApply(internalFrom, internalTo);
    setActiveField('from');
    setIsOpen(false);
  };

  const getDayClassNames = (day) => {
    const classes = ['dri-day'];
    if (!day) {
      classes.push('dri-day--empty'); return classes.join(' ');
    }
    if (isDayDisabled(day)) classes.push('dri-day--disabled');
    if (isDaySelected(day)) classes.push('dri-day--selected');
    if (isDayInRange(day)) classes.push('dri-day--in-range');
    if (isRangeStart(day)) classes.push('dri-day--range-start');
    if (isRangeEnd(day)) classes.push('dri-day--range-end');
    return classes.join(' ');
  };

  const displayValue = useMemo(() => {
    if (internalFrom && internalTo) return `${internalFrom.format(dateFormat)} - ${internalTo.format(dateFormat)}`;
    if (internalFrom) return internalFrom.format(dateFormat);
    return '';
  }, [internalFrom, internalTo, dateFormat]);

  const handleKeyToggle = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div className="dri-wrapper" ref={wrapperRef}>
      {/* ── Trigger ──────────────────────────────────────────────── */}
      <div className="dri-field-group">
        {label && (
          <span className="dri-label">
            {label}
            {isRequired && <span className="asteric">*</span>}
          </span>
        )}
        <div
          className={`dri-input${isOpen ? ' dri-input--active' : ''}`}
          onClick={() => setIsOpen((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyToggle}
        >
          <span className={`dri-input-text${!displayValue ? ' dri-input-text--placeholder' : ''}`}>
            {displayValue || placeholder}
          </span>
          <img src={CalendarIcon} alt="calendar" className="dri-cal-icon" />
        </div>
      </div>

      {/* ── Dropdown popup ───────────────────────────────────────── */}
      {isOpen && (
        <div className="dri-popup">
          <div className="dri-container">

            {/* From / To inputs — optional */}
            {showFromTo && (
              <div className="dri-fields">
                <div className="dri-field-group">
                  <span className="dri-label">
                    {fromLabel}{isRequired && <span className="asteric">*</span>}
                  </span>
                  <div
                    className={`dri-input${activeField === 'from' ? ' dri-input--active' : ''}`}
                    onClick={() => setActiveField('from')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveField('from');
                      }
                    }}
                  >
                    <span className={`dri-input-text${!internalFrom ? ' dri-input-text--placeholder' : ''}`}>
                      {internalFrom ? internalFrom.format(dateFormat) : ''}
                    </span>
                    <img src={CalendarIcon} alt="calendar" className="dri-cal-icon" />
                  </div>
                </div>

                <div className="dri-field-group">
                  <span className="dri-label">
                    {toLabel}
                    {isRequired && <span className="asteric">*</span>}
                  </span>
                  <div
                    className={`dri-input${activeField === 'to' ? ' dri-input--active' : ''}`}
                    onClick={() => setActiveField('to')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveField('to');
                      }
                    }}
                  >
                    <span className={`dri-input-text${!internalTo ? ' dri-input-text--placeholder' : ''}`}>
                      {internalTo ? internalTo.format(dateFormat) : 'To'}
                    </span>
                    <img src={CalendarIcon} alt="calendar" className="dri-cal-icon" />
                  </div>
                </div>
              </div>
            )}

            {/* Month / Year header */}
            <div className="dri-cal-header">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={viewMonth}
                  onChange={(e) => setViewMonth(Number(e.target.value))}
                  input={<OutlinedInput />}
                  MenuProps={{
                    disablePortal: true,
                    onClick: (e) => e.stopPropagation(),
                    slotProps: {
                      paper: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    },
                  }}
                >
                  {monthOptions.map(({ name, index }) => (
                    <MenuItem key={index} value={index}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={viewYear}
                  onChange={(e) => setViewYear(Number(e.target.value))}
                  input={<OutlinedInput />}
                  MenuProps={{
                    disablePortal: true,
                    onClick: (e) => e.stopPropagation(),
                    slotProps: {
                      paper: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    },
                  }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Week day labels */}
            <div className="dri-week-header">
              {WEEK_DAYS.map((d, i) => (
                <span key={i} className="dri-week-day">{d}</span>
              ))}
            </div>

            {/* Days grid */}
            <div className="dri-days-grid">
              {calendarDays.map((day, i) => (
                <span
                  key={i}
                  className={getDayClassNames(day)}
                  onClick={() => handleDayClick(day)}
                  role={day ? 'button' : undefined}
                  tabIndex={day ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (day && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleDayClick(day);
                    }
                  }}
                >
                  {day || ''}
                </span>
              ))}
            </div>

            <div className="dri-divider" />

            <div className="dri-footer">
              <ButtonComponent
                label="Reset"
                variant="text"
                onClick={handleReset}
                btnCss="dri-reset-btn"
                id="dri-reset"
              />
              <ButtonComponent
                label="Apply"
                variant="contained"
                onClick={handleApply}
                btnCss="dri-apply-btn"
                id="dri-apply"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DateRangeInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  showFromTo: PropTypes.bool,
  isRequired: PropTypes.bool,
  fromLabel: PropTypes.string,
  toLabel: PropTypes.string,
  fromValue: PropTypes.object,
  toValue: PropTypes.object,
  onChange: PropTypes.func,
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  minMonth: PropTypes.number,
  maxMonth: PropTypes.number,
  minDay: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object, PropTypes.string]),
  maxDay: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object, PropTypes.string]),
  dateFormat: PropTypes.string,
  saveOnOutsideClick: PropTypes.bool,
};

export default DateRangeInput;
